import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  Divider,
  Tooltip,
  Switch,
  TextField,
  FormControlLabel,
  Button,
  Alert,
  Snackbar,
  InputAdornment,
} from '@mui/material';
import {
  SettingsRounded as SettingsIcon,
  CloseRounded as CloseIcon,
  SaveRounded as SaveIcon,
  RestartAltRounded as ResetIcon,
} from '@mui/icons-material';

// ============================================
// TYPES
// ============================================

interface ManagerSettings {
  limitMovementsEnabled: boolean;
  limitMovementsValue: number;
  minDocumentAmountEnabled: boolean;
  minDocumentAmountValue: number;
  minDocumentPositionsEnabled: boolean;
  minDocumentPositionsValue: number;
}

const DEFAULT_SETTINGS: ManagerSettings = {
  limitMovementsEnabled: false,
  limitMovementsValue: 1,
  minDocumentAmountEnabled: false,
  minDocumentAmountValue: 1000,
  minDocumentPositionsEnabled: false,
  minDocumentPositionsValue: 3,
};

// ============================================
// SETTING ROW COMPONENT
// ============================================

interface SettingRowProps {
  label: string;
  description?: string;
  enabled: boolean;
  onToggle: (value: boolean) => void;
  children?: React.ReactNode;
}

const SettingRow: React.FC<SettingRowProps> = ({ label, description, enabled, onToggle, children }) => (
  <Box sx={{ py: 2.5, px: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" fontWeight={600} color="text.primary">
          {label}
        </Typography>
        {description && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
            {description}
          </Typography>
        )}
      </Box>
      <Switch
        checked={enabled}
        onChange={(e) => onToggle(e.target.checked)}
        size="small"
        color="primary"
      />
    </Box>

    {enabled && children && (
      <Box sx={{ mt: 1.5 }}>
        {children}
      </Box>
    )}
  </Box>
);

// ============================================
// MAIN COMPONENT
// ============================================

const AdminSettingsPanel: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<ManagerSettings>({ ...DEFAULT_SETTINGS });
  const [saved, setSaved] = useState(false);

  const handleToggle = (key: keyof ManagerSettings) => (value: boolean) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleValue = (key: keyof ManagerSettings) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const num = parseFloat(e.target.value);
    if (!isNaN(num) && num >= 0) {
      setSettings(prev => ({ ...prev, [key]: num }));
    }
  };

  const handleSave = () => {
    // Mock save — replace with API call when ready
    console.log('Сохранение настроек:', settings);
    setSaved(true);
  };

  const handleReset = () => {
    setSettings({ ...DEFAULT_SETTINGS });
  };

  return (
    <>
      {/* Кнопка в тулбаре */}
      <Tooltip title="Настройки">
        <IconButton
          onClick={() => setOpen(true)}
          size="small"
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Drawer с настройками */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 420 }, bgcolor: '#fafafa' }
        }}
      >
        {/* Заголовок */}
        <Box sx={{
          px: 3, py: 2.5, bgcolor: '#ffffff',
          borderBottom: '1px solid', borderColor: 'divider',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SettingsIcon color="action" />
            <Typography variant="h6" fontWeight={700} color="text.primary">
              Настройки
            </Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} size="small" edge="end">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
          {/* Секция: Настройки менеджера */}
          <Box sx={{ px: 3, pt: 2.5, pb: 0.5 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: '0.08em', fontWeight: 700 }}
            >
              Настройки для менеджера
            </Typography>
          </Box>

          <Box sx={{ bgcolor: '#ffffff', border: '1px solid', borderColor: 'divider', mx: 2, borderRadius: 2, overflow: 'hidden' }}>

            {/* 1. Ограничение перемещений */}
            <SettingRow
              label="Ограничение перемещений по одному направлению"
              description="Аптека не сможет создать больше указанного числа перемещений между одними и теми же аптеками"
              enabled={settings.limitMovementsEnabled}
              onToggle={handleToggle('limitMovementsEnabled')}
            >
              <TextField
                size="small"
                label="Максимум перемещений"
                type="number"
                value={settings.limitMovementsValue}
                onChange={handleValue('limitMovementsValue')}
                inputProps={{ min: 1, step: 1 }}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end"></InputAdornment>
                }}
              />
            </SettingRow>

            <Divider />

            {/* 2. Минимальная сумма документа */}
            <SettingRow
              label="Минимальная сумма документа"
              description="Документы с суммой документа ниже указанной не будут приняты к обработке"
              enabled={settings.minDocumentAmountEnabled}
              onToggle={handleToggle('minDocumentAmountEnabled')}
            >
              <TextField
                size="small"
                label="Минимальная сумма"
                type="number"
                value={settings.minDocumentAmountValue}
                onChange={handleValue('minDocumentAmountValue')}
                inputProps={{ min: 0, step: 100 }}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end"></InputAdornment>
                }}
              />
            </SettingRow>

            <Divider />

            {/* 3. Минимальное количество позиций */}
            <SettingRow
              label="Минимальное количество позиций в документе"
              description="Документы с меньшим числом позиций не будут приняты к обработке"
              enabled={settings.minDocumentPositionsEnabled}
              onToggle={handleToggle('minDocumentPositionsEnabled')}
            >
              <TextField
                size="small"
                label="Минимум позиций"
                type="number"
                value={settings.minDocumentPositionsValue}
                onChange={handleValue('minDocumentPositionsValue')}
                inputProps={{ min: 1, step: 1 }}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end"></InputAdornment>
                }}
              />
            </SettingRow>

          </Box>

          Это демонтрация не имеющая ничего общего с рабочим функционалом
        </Box>

        {/* Footer */}
        <Box sx={{
          px: 3, py: 2, bgcolor: '#ffffff',
          borderTop: '1px solid', borderColor: 'divider',
          display: 'flex', gap: 1.5, justifyContent: 'flex-end'
        }}>
          <Tooltip title="Сбросить к значениям по умолчанию">
            <IconButton size="small" onClick={handleReset} color="default">
              <ResetIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disableElevation
          >
            Сохранить
          </Button>
        </Box>
      </Drawer>

      {/* Snackbar подтверждение */}
      <Snackbar
        open={saved}
        autoHideDuration={3000}
        onClose={() => setSaved(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSaved(false)} sx={{ width: '100%' }}>
          Настройки сохранены
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdminSettingsPanel;
